import { Editor, Plugin } from 'grapesjs';
import DocumentGenerator from 'shared/documents/DocumentGenerator';
import { DocumentTemplateEntityType } from 'types/graphql';
import { textStyles, variants } from './layout';

const plugin: (entityType: DocumentTemplateEntityType) => Plugin<{}> = (_entityType) => (editor: Editor) => {
  const blockManager = editor.BlockManager;
  const domComponents = editor.DomComponents;

  blockManager.add('variable', {
    label: 'Variable',
    content: `<div data-gjs-type="variable"></div>`,
    media: `
        <svg viewBox="0 0 24 24">
          <path d="M9.4 16.6 4.8 12l4.6-4.6L8 6l-6 6 6 6zm5.2 0 4.6-4.6-4.6-4.6L16 6l6 6-6 6z" />
        </svg>
      `,
    category: 'Variables',
    attributes: { variablePath: null },
  });

  blockManager.add('pageNumber', {
    label: 'Page Number',
    category: 'Variables',
    content: '<pagenumber>#</pagenumber>',
  });

  blockManager.add('totalPages', {
    label: 'Total Pages',
    category: 'Variables',
    content: '<totalpages>#</totalpages>',
  });

  const variableTraits = (variable?: string) => [
    { type: 'text', label: 'Variable', name: 'variable', value: variable, changeProp: true },
    { type: 'text', label: 'Prefix', name: 'prefix', changeProp: true },
    { type: 'text', label: 'Suffix', name: 'suffix', changeProp: true },
    {
      type: 'select',
      label: 'Variant',
      name: 'variant',
      options: variants.map(({ label, value, style }) => ({ id: value, label, style })),
      changeProp: true,
      default: 'content',
    },
  ];

  editor.on('documentTypeChanged', (entityType = _entityType) => {
    const definitionMap = DocumentGenerator.getDefinitions(entityType);

    if (!definitionMap) {
      return;
    }

    const existingBlocks = blockManager
      .getAll()
      .filter((block) => block.get('category') === 'Variables' && block.get('attributes').variablePath !== null);
    existingBlocks.forEach((block) => blockManager.remove(block.get('id')));

    const createVariableComponents = (definitionMap, path = '', parent = null) => {
      Object.entries(definitionMap).forEach(([variable, { label, children }]) => {
        blockManager.add(`variable-${path}-${variable}`, {
          label,
          category: parent ? `${parent} Children` : 'Variables',
          content: () => {
            return {
              components: {
                type: 'variable',
                variable: variable,
                label,
                variant: 'content',
                attributes: { path: path ? `${path}.${variable}` : variable, class: 'typography-content' },
              },
            };
          },
        });

        if (children) {
          createVariableComponents(children, path ? `${path}.${variable}` : variable, label);
        }
      });
    };

    createVariableComponents(definitionMap);
  });

  domComponents.addType('variable', {
    model: {
      defaults: {
        tagName: 'div',
        droppable: true,
        draggable: true,
        attributes: { 'data-gjs-type': 'variable' },
        styles: textStyles,
        traits: variableTraits(),
      },
      init() {
        this.listenTo(this, 'change:variable', this.handleContentChange);
        this.listenTo(this, 'change:prefix', this.handleContentChange);
        this.listenTo(this, 'change:suffix', this.handleContentChange);
        this.listenTo(this, 'change:variant', this.handleTypographyClassChange);
      },
      handleContentChange() {
        this.view.render();
      },
      toHTML: function () {
        const prefix = this.get('prefix') ?? '';
        const suffix = this.get('suffix') ?? '';
        const variable = this.get('variable');
        const classes = this.get('classes').pluck('name').join(' ');

        return `<div id="${this.ccid}">
          <span class="${classes}">${prefix}{{{${variable}}}}${suffix}</span>
        </div>`;
      },
      handleTypographyClassChange() {
        const selectedType = this.get('variant');

        variants.forEach(({ className }) => this.removeClass(className));

        if (selectedType) {
          const className = variants.find((v) => v.value === selectedType)?.className;

          this.addClass(className);
        }
      },
    },
    view: {
      onRender: function () {
        const prefix = this.model.get('prefix') || '';
        const suffix = this.model.get('suffix') || '';
        const variable = this.model.get('variable') || '';

        this.el.innerHTML = `${prefix}${variable}${suffix}` || '<i>select variable</i>';
      },
    },
  });
};

export default plugin;
